import { Trophy, Scales, CircleNotch } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { VoiceButton } from "@/components/VoiceButton";
import { ModelPicker } from "@/components/ModelPicker";
import { TargetSelect } from "@/components/Controls";
import { OutputTerminal } from "@/components/OutputTerminal";
import { MetricsPanel } from "@/components/MetricsPanel";

export function CompareView({ s, config, onCompare, onShare, onSave }) {
  const winner = s.cmp ? (s.cmp.a.quality_score >= s.cmp.b.quality_score ? "a" : "b") : null;
  return (
    <div className="space-y-6">
      <div className="stp-card rounded-3xl p-6 sm:p-8" data-testid="compare-config-panel">
        <div className="mb-3 flex items-center justify-between">
          <label className="text-sm font-semibold text-ink">Your idea</label>
          <VoiceButton testId="compare-voice-button" onResult={(t) => s.setInput((p) => (p ? p + " " + t : t))} disabled={s.cmpLoading || !s.hasAccess} />
        </div>
        <Textarea data-testid="compare-input-textarea" value={s.input} onChange={(e) => s.setInput(e.target.value)} placeholder="Describe your goal, then see how two different AIs write the prompt…" className="min-h-[110px] resize-none rounded-2xl border-black/10 bg-[#FAFAFC] text-[15px] text-ink placeholder:text-mist/70 focus-visible:ring-brand/30" />
        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-mist">Made for</label>
            <TargetSelect tools={config.target_tools || []} value={s.targetTool} onChange={s.setTargetTool} testId="compare-target-select" />
          </div>
          <div className="rounded-2xl bg-[#FAFAFC] p-3"><p className="mb-2 text-xs font-semibold text-brand">Model A</p><ModelPicker providers={config.providers} provider={s.provA} model={s.modelA} onProvider={s.setProvA} onModel={s.setModelA} idPrefix="cmp-a" stacked /></div>
          <div className="rounded-2xl bg-[#FAFAFC] p-3"><p className="mb-2 text-xs font-semibold text-[#C93400]">Model B</p><ModelPicker providers={config.providers} provider={s.provB} model={s.modelB} onProvider={s.setProvB} onModel={s.setModelB} idPrefix="cmp-b" stacked /></div>
        </div>
        <button data-testid="compare-button" onClick={onCompare} disabled={s.cmpLoading} className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-semibold text-white transition-transform hover:bg-black active:scale-[0.99] disabled:opacity-60">
          {s.cmpLoading ? <><CircleNotch size={18} className="animate-spin" /> Comparing…</> : <><Scales size={18} weight="fill" /> Compare prompts</>}
        </button>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="compare-results">
        {["a", "b"].map((key) => {
          const res = s.cmp?.[key];
          return (
            <div key={key} className="flex flex-col gap-4">
              <div className="flex h-8 items-center justify-between px-1">
                <span className="text-sm font-semibold text-ink">Model {key.toUpperCase()} · <span className="text-mist">{res?.model || (key === "a" ? s.modelA : s.modelB)}</span></span>
                {winner === key && <span data-testid={`winner-${key}`} className="flex items-center gap-1 rounded-full bg-[#FFF4E5] px-3 py-1 text-xs font-semibold text-[#C93400]"><Trophy size={13} weight="fill" /> Stronger</span>}
              </div>
              <OutputTerminal title={res ? `Score ${res.quality_score}` : "Waiting"} text={res?.prompt || ""} loading={s.cmpLoading} empty={!s.cmpLoading && !res} compact testId={`compare-output-${key}`} target={s.targetTool} onShare={res?.id ? () => onShare(res) : undefined} onSave={res ? () => onSave(res) : undefined} />
              {res && !s.cmpLoading && <MetricsPanel result={res} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
